import { useMutation, useQueryClient } from '@tanstack/react-query';
import CmsService from '@/services/CmsService';
import type {
  AlignmentInput, EligibilityCheckInput, ReportingSubmissionInput,
} from '@/services/CmsService';
import { TASK_KEYS } from '@/state/data/task/keys';
import { PATIENT_FILE_KEYS } from '@/state/data/patientFile/keys';
import { CMS_KEYS } from './keys';

// App-driven unalignment (unalignment_requested → WF5 → WF24). Async at apiv2:
// this only fires the event (acks immediately). The CMS $unalign verdict + WF24's
// care-period PATCH land out-of-band — CmsUnalignmentModal polls
// useCmsSubmissionPoll('unalign') and invalidates CARE_PERIOD/CMS/TASK keys itself
// once a terminal row appears (no eager invalidate here: the care-period status
// hasn't changed yet at ack time).
export function useTriggerCmsUnalignment() {
  return useMutation({
    mutationFn: (vars: {
      withingsUserId: string | number;
      carePeriodId?: string | number;
      reason: string;
      conditions?: string[];
    }) => CmsService.triggerCmsUnalignment(
      vars.withingsUserId,
      vars.carePeriodId,
      vars.reason,
      vars.conditions,
    ),
  });
}

// App-driven alignment (cms_alignment_requested → WF13 → WF12). Async at apiv2:
// this only fires the event (acks immediately). The verdict + WF13 cascade land
// out-of-band — the modal polls useCmsSubmissionPoll('align') and invalidates TASK/CMS/
// care-period keys itself once a terminal 'success' row appears (no eager
// invalidate here: the cascade hasn't run yet at ack time).
export function useFireAlignment() {
  return useMutation({
    mutationFn: (input: AlignmentInput) => CmsService.fireAlignment(input),
  });
}

// Eligibility submit is fire-and-poll (apiv2 acks at ~5s SYNC_TIMEOUT; the CMS
// verdict + WF12 side effects — auto-complete onb_eligibility_check, attach
// context — land out-of-band ~12-16s later). So NO eager invalidate here: it
// would re-read stale task/CMS rows before WF12 completes them. The modal polls
// useCmsSubmissionPoll('eligibility') and invalidates TASK/CMS keys itself once
// the terminal verdict row appears (preview writes nothing, so it needs none).
export function useRunCmsEligibilityCheck() {
  return useMutation({
    mutationFn: (input: EligibilityCheckInput) => CmsService.runCmsEligibilityCheck(input),
  });
}

export function useRunCmsReportingSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReportingSubmissionInput) => CmsService.runCmsReportingSubmission(input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: TASK_KEYS.all });
      qc.invalidateQueries({ queryKey: CMS_KEYS.all });
      // A submit carrying manual values wrote measure_state / lab_result rows — the patient
      // file must show them without a reload. Preview writes nothing, so nothing to refresh.
      if (vars.mode !== 'preview' && (vars.manualObservations || []).length > 0) {
        qc.invalidateQueries({ queryKey: PATIENT_FILE_KEYS.all });
      }
    },
  });
}
